import React, { useEffect } from 'react';
import { END } from 'redux-saga';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import isbot from 'isbot';

import PageLayout from '../../../../components/layouts/Main/PageLayout';
import { wrapper } from '../../../../redux/store';
import { get } from '../../../../api';
import { cleanJobRequest, errorJobRequest, loadedJobRequest, loadJobRequest } from '../../../../redux/actions/jobs';
import SentryAPI from '../../../../api/SentryAPI';
import SEO from '../../../../components/molecules/Seo';
import { PageLoader } from '../../../../components/atoms/pageLoader';
import { currentJobSelector, jobsLoadingSelector } from '../../../../redux/selectors/jobs';
import { NetworkSchema } from '../../../../schema/network';
import { getSSRProtocolAndHost } from '../../../../helpers/ssrHelpers';
import { ProtocolSchema } from '../../../../schema/protocol';

import JobPosting from '../../../../components/atoms/jobPosting';
import Job from '../../../../containers/job';

const Index = ({ network, protocol, host }) => {
  const dispatch = useDispatch();
  const currentJob = useSelector(currentJobSelector);
  const { title, organization } = currentJob || {};
  const { name: organizationName } = organization || {};
  const loading = useSelector(jobsLoadingSelector);
  const router = useRouter();
  const {
    query: { jobSlug },
  } = router;

  useEffect(() => {
    const { id } = currentJob || {};
    if (!loading && !id) {
      dispatch(loadJobRequest({ jobSlug, network }));
    }
  }, [network, loading, currentJob, jobSlug, dispatch]);

  useEffect(() => () => dispatch(cleanJobRequest()), [dispatch]);

  if (currentJob && loading) {
    return <PageLoader />;
  }

  return (
    <>
      {currentJob && (
        <>
          <SEO protocol={protocol} host={host} network={network} title={`${title} @ ${organizationName}`} />
          <JobPosting job={currentJob} network={network} />
          <Job currentJob={currentJob} network={network} />
        </>
      )}
    </>
  );
};

Index.propTypes = {
  network: NetworkSchema.isRequired,
  protocol: ProtocolSchema.isRequired,
  host: PropTypes.string.isRequired,
};

Index.Layout = PageLayout;

export const getServerSideProps = wrapper.getServerSideProps((store) => async (ctx) => {
  const { req, query } = ctx;
  const { network, advancedDesign } = req;
  const { jobSlug } = query;
  const { protocol, host } = getSSRProtocolAndHost(ctx);

  if (network?.id) {
    try {
      // todo: this should move to the getroAPI for consistency
      const { data } = await get(`/jobs/${jobSlug}?collection_id=${network.id}`);

      store.dispatch(loadedJobRequest(data));
    } catch (e) {
      const { response } = e;
      store.dispatch(errorJobRequest(e));
      if (response && response.status === 404) {
        store.dispatch(END);
        await store.sagaTask.toPromise();
        return { props: { network, advancedDesign }, notFound: true };
      }

      SentryAPI.trackError('fetch job', {
        _href: req.asPath,
        _isBot: isbot(req.headers['user-agent']),
        networkId: network.id,
        jobSlug,
        e,
      });
    }
  }

  // check here if it's a company detail modal open and if so fetch the details
  store.dispatch(END);
  await store.sagaTask.toPromise();
  return {
    props: { protocol, host },
  };
});

export default Index;
